'use client';
import { Link as HeroUiLink, LinkProps as HeroUiLinkProps } from '@heroui/link';
import { HTMLHeroUIProps } from '@heroui/system';
import { default as NextLink, LinkProps as NextLinkProps } from 'next/link';
import { useParams } from 'next/navigation';
import { forwardRef, PropsWithChildren, useEffect, useState } from 'react';
import type { UrlObject } from 'url';
import { useDefaultLocale } from '@/providers/root-provider';
import { getLocaleCookie } from '@/utils/locale-cookie';

type Url = string | UrlObject;

type InternalLinkProps = Omit<NextLinkProps, 'passHref' | 'legacyBehavior'> & {
  /**
   * The path or URL to navigate to. It can also be an object.
   */
  href: Url;
  /**
   * Optional decorator for the path that will be shown in the browser URL bar.
   */
  as?: Url;
  /**
   * Replace the current `history` state instead of adding a new url into the stack.
   * @defaultValue `false`
   */
  replace?: boolean;
  /**
   * Whether to override the default scroll behavior
   * @defaultValue `true`
   */
  scroll?: boolean;
  /**
   * Update the path of the current page without rerunning data fetching methods.
   * @defaultValue `false`
   */
  shallow?: boolean;
  /**
   * Prefetch the page in the background.
   * @defaultValue `true`
   */
  prefetch?: boolean;
  /**
   * The active locale is automatically prepended. `locale` allows for providing a different locale.
   * When `false` `href` has to include the locale as the default behavior is disabled.
   */
  locale?: string | false;
  /**
   * Optional event handler for when the mouse pointer is moved onto Link
   */
  onMouseEnter?: React.MouseEventHandler<HTMLAnchorElement>;
  /**
   * Optional event handler for when Link is touched.
   */
  onTouchStart?: React.TouchEventHandler<HTMLAnchorElement>;
  /**
   * Optional event handler for when Link is clicked.
   */
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
};

interface CustomLinkProps {
  /**
   * Whether the link is external.
   * @default false
   */
  isExternal?: boolean;
  /**
   * Whether to show the icon when the link is external.
   * @default false
   */
  showAnchorIcon?: boolean;
  /**
   * The icon to display right after the link.
   * @default <LinkIcon />
   */
  anchorIcon?: React.ReactNode;
  /**
   * CSS class name for styling
   */
  className?: string;
  /**
   * Custom props
   */
  withoutLocale?: boolean;
  noStyle?: boolean;
}

type LinkComponentProps = PropsWithChildren<
  InternalLinkProps & CustomLinkProps & HeroUiLinkProps & HTMLHeroUIProps<'a'>
>;

export const Link = forwardRef<HTMLAnchorElement, LinkComponentProps>(function Link(
  {
    href,
    as,
    replace,
    scroll = true,
    shallow,
    prefetch,
    onMouseEnter,
    onTouchStart,
    onClick,
    withoutLocale,
    children,
    noStyle,
    className,
    ...rest
  },
  ref,
) {
  const { locale } = useParams();
  const defaultLocale = useDefaultLocale();

  // The `locale` cookie is only readable in the browser (`document.cookie`), so
  // reading it during the initial render makes the server (no cookie -> URL
  // param/default locale) and the first client render (cookie locale) disagree
  // on the prefixed `href` — e.g. `/hu/` vs `/ro/` — which trips React
  // hydration. Defer the cookie read until after mount: the first client render
  // uses the same locale the server used, then we upgrade to the
  // cookie-preferred locale (LocaleFirstBootstrap realigns the URL to it in
  // parallel anyway).
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => setHasMounted(true), []);

  const cookieLocale = hasMounted ? getLocaleCookie()?.trim().toLowerCase() : undefined;
  const paramLocale = typeof locale === 'string' ? locale.toLowerCase() : undefined;
  const effectiveLocale = cookieLocale ?? paramLocale ?? defaultLocale?.toLowerCase() ?? 'en';

  const finalHref = withoutLocale
    ? href
    : typeof href === 'string'
      ? `/${effectiveLocale}${href.startsWith('/') ? href : `/${href}`}`
      : href;

  // For noStyle links, use Next.js Link directly without HeroUI wrapper. We forward `ref`
  // and spread the remaining props onto `NextLink` so overlay components from HeroUI
  // (Tooltip / Popover / Dropdown) — which clone their trigger child via `cloneElement`
  // to inject pointer/focus/aria handlers — can actually attach those handlers to the
  // underlying anchor. Without this passthrough the trigger element receives no hover
  // listeners and the overlay never opens.
  if (noStyle) {
    return (
      <NextLink
        ref={ref}
        href={finalHref}
        as={as}
        replace={replace}
        scroll={scroll}
        shallow={shallow}
        prefetch={prefetch}
        onMouseEnter={onMouseEnter}
        onTouchStart={onTouchStart}
        onClick={onClick}
        className={className}
        {...rest}
      >
        {children}
      </NextLink>
    );
  }

  // For styled links, use NextLink with HeroUI Link as child
  // Next.js 13+ automatically handles the anchor tag generation

  return (
    <HeroUiLink as="span" className={className} {...rest} href={undefined}>
      <NextLink
        ref={ref}
        href={finalHref}
        replace={replace}
        scroll={scroll}
        shallow={shallow}
        prefetch={prefetch}
        onMouseEnter={onMouseEnter}
        onTouchStart={onTouchStart}
        onClick={onClick}
        className="w-full"
      >
        {children}
      </NextLink>
    </HeroUiLink>
  );
});

Link.displayName = 'Link';
